import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Car, 
  Copy, 
  ExternalLink, 
  Save, 
  Trash2, 
  ChevronDown,
  Fuel,
  Gauge,
  Calendar,
  MapPin,
  Cog,
  Zap,
  Bookmark,
  Search,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Polish translations
const PL = {
  // Header
  appName: "Hellboy's Garage",
  appSubtitle: "Wyszukiwarka samochodów",
  reset: "Resetuj",
  
  // Vehicle section
  vehicle: "Pojazd",
  brand: "Marka",
  model: "Model",
  selectBrand: "Wybierz markę",
  selectModel: "Wybierz model",
  selectBrandFirst: "Najpierw wybierz markę",
  
  // Price & Year section
  priceAndYear: "Cena i Rok",
  minPrice: "Cena min (€)",
  maxPrice: "Cena max (€)",
  yearFrom: "Rok od",
  yearTo: "Rok do",
  any: "Dowolny",
  
  // Engine section
  engineAndTransmission: "Silnik i Skrzynia",
  fuelType: "Rodzaj paliwa",
  gearbox: "Skrzynia biegów",
  minHp: "Min KM",
  maxHp: "Max KM",
  minMileage: "Min przebieg (km)",
  maxMileage: "Max przebieg (km)",
  
  // Seller type section
  sellerType: "Typ sprzedawcy",
  sellerAll: "Wszystkie",
  sellerPrivate: "Prywatne",
  sellerDealer: "Dealerzy",
  
  // Location section
  location: "Lokalizacja",
  postalCode: "Kod pocztowy",
  radius: "Promień (km)",
  anyDistance: "Dowolna odległość",
  
  // Search section
  searchAllPlatforms: "Szukaj na wszystkich platformach",
  generateAllUrls: "Generuj linki",
  generatedLinks: "Wygenerowane linki",
  openAllInNewTabs: "Otwórz wszystkie w nowych kartach",
  saveSearchCriteria: "Zapisz kryteria wyszukiwania",
  
  // Saved searches
  savedSearches: "Zapisane wyszukiwania",
  saved: "zapisanych",
  noSavedSearches: "Brak zapisanych wyszukiwań",
  generateAndSave: "Wygeneruj link i zapisz go na później",
  
  // Save dialog
  saveSearchPreset: "Zapisz szablon wyszukiwania",
  presetName: "Nazwa szablonu",
  presetPlaceholder: "np. BMW M3 do 50 tys.",
  savePreset: "Zapisz szablon",
  
  // Messages
  urlCopied: "URL skopiowany do schowka!",
  urlsCopied: "URL skopiowany!",
  searchSaved: "Wyszukiwanie zapisane!",
  searchDeleted: "Wyszukiwanie usunięte",
  loaded: "Załadowano",
  urlsGenerated: "Linki wygenerowane dla wszystkich 3 platform!",
  failedToCopy: "Nie udało się skopiować. Skopiuj URL ręcznie.",
  popupBlocked: "Popup zablokowany! URL został skopiowany - wklej go w przeglądarce.",
  enterPresetName: "Wprowadź nazwę szablonu",
  
  // Limitations
  kleinanzeigenLimit: "Obsługiwane tylko: marka, model, paliwo, skrzynia i sprzedawca. Cenę, rok, przebieg i KM ustaw ręcznie na stronie.",
  mobileDeLimit: "Filtr 'Typ sprzedawcy' nie jest obsługiwany przez URL. Ustaw go ręcznie na stronie.",
  
  // Footer
  footerText: "To narzędzie generuje linki wyszukiwania dla mobile.de, kleinanzeigen.de i autoscout24.de. Nie jest powiązane z żadną z tych platform.",
  
  // Fuel types (for display)
  petrol: "Benzyna",
  diesel: "Diesel",
  electric: "Elektryczny",
  hybrid: "Hybryda",
  lpg: "LPG",
  cng: "CNG",
  
  // Gearbox types
  automatic: "Automatyczna",
  manual: "Manualna",
  
  // Price suffix
  upTo: "Do"
};

const SearchBuilder = () => {
  // State for form data
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [gearboxTypes, setGearboxTypes] = useState([]);
  const [sellerTypes, setSellerTypes] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [generatedUrls, setGeneratedUrls] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState("");

  // Form state
  const [criteria, setCriteria] = useState({
    brand: "",
    model: "",
    price_min: "",
    price_max: "",
    year_min: "",
    year_max: "",
    mileage_min: "",
    mileage_max: "",
    fuel_type: "",
    gearbox: "",
    hp_min: "",
    hp_max: "",
    location: "",
    radius: "",
    seller_type: ""
  });

  // Fetch initial data
  useEffect(() => {
    fetchBrands();
    fetchFuelTypes();
    fetchGearboxTypes();
    fetchSellerTypes();
    fetchSavedSearches();
  }, []);

  // Fetch models when brand changes
  useEffect(() => {
    if (criteria.brand) {
      fetchModels(criteria.brand);
    } else {
      setModels([]);
    }
  }, [criteria.brand]);

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data.brands);
    } catch (error) {
      toast.error("Nie udało się załadować marek");
    }
  };

  const fetchModels = async (brand) => {
    try {
      const response = await axios.get(`${API}/models/${encodeURIComponent(brand)}`);
      setModels(response.data.models);
    } catch (error) {
      toast.error("Nie udało się załadować modeli");
    }
  };

  const fetchFuelTypes = async () => {
    try {
      const response = await axios.get(`${API}/fuel-types`);
      setFuelTypes(response.data.fuel_types);
    } catch (error) {
      toast.error("Nie udało się załadować typów paliwa");
    }
  };

  const fetchGearboxTypes = async () => {
    try {
      const response = await axios.get(`${API}/gearbox-types`);
      setGearboxTypes(response.data.gearbox_types);
    } catch (error) {
      toast.error("Nie udało się załadować typów skrzyni");
    }
  };

  const fetchSellerTypes = async () => {
    try {
      const response = await axios.get(`${API}/seller-types`);
      setSellerTypes(response.data.seller_types);
    } catch (error) {
      console.error("Nie udało się załadować typów sprzedawcy");
    }
  };

  const fetchSavedSearches = async () => {
    try {
      const response = await axios.get(`${API}/searches`);
      setSavedSearches(response.data);
    } catch (error) {
      console.error("Nie udało się załadować zapisanych wyszukiwań");
    }
  };

  const handleInputChange = (field, value) => {
    setCriteria(prev => ({
      ...prev,
      [field]: value,
      // Reset model when brand changes
      ...(field === "brand" ? { model: "" } : {})
    }));
  };

  const generateUrl = async () => {
    setIsLoading(true);
    try {
      const cleanCriteria = {};
      Object.entries(criteria).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          // Convert numeric fields
          if (["price_min", "price_max", "year_min", "year_max", "mileage_min", "mileage_max", "hp_min", "hp_max", "radius"].includes(key)) {
            cleanCriteria[key] = parseInt(value);
          } else {
            cleanCriteria[key] = value;
          }
        }
      });

      const response = await axios.post(`${API}/generate-all-urls`, cleanCriteria);
      setGeneratedUrls(response.data);
      toast.success(PL.urlsGenerated);
    } catch (error) {
      toast.error("Nie udało się wygenerować linków");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (url, platform) => {
    // Method 1: Try modern clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(url);
        toast.success(`${platform} - ${PL.urlCopied}`);
        return;
      } catch (err) {
        console.log("Clipboard API failed, trying fallback...");
      }
    }
    
    // Method 2: Fallback using textarea and execCommand
    try {
      const textArea = document.createElement("textarea");
      textArea.value = url;
      
      // Make it invisible but still copyable
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.width = "2em";
      textArea.style.height = "2em";
      textArea.style.padding = "0";
      textArea.style.border = "none";
      textArea.style.outline = "none";
      textArea.style.boxShadow = "none";
      textArea.style.background = "transparent";
      textArea.style.opacity = "0";
      
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success(`${platform} - ${PL.urlCopied}`);
        return;
      }
    } catch (err) {
      console.log("execCommand fallback failed");
    }
    
    // Method 3: Last resort - prompt user
    toast.error(PL.failedToCopy);
  };

  const openInNewTab = (url) => {
    // Kleinanzeigen works fine with direct opening - skip the redirect trick
    if (url.includes("kleinanzeigen.de")) {
      const newWindow = window.open(url, "_blank", "noopener,noreferrer");
      if (!newWindow) {
        copyToClipboard(url, "URL");
        toast.error(PL.popupBlocked);
      }
      return;
    }
    
    // For mobile.de and autoscout24 - use redirect trick to bypass app interception
    // Copy URL first as backup (in case app opens without criteria)
    copyToClipboard(url, "URL");
    
    // Try to force browser by using a data URL redirect
    // This creates a small HTML page that redirects to the actual URL
    const redirectHtml = `
      <html>
        <head>
          <meta http-equiv="refresh" content="0;url=${encodeURIComponent(url)}">
        </head>
        <body>
          <script>window.location.href = decodeURIComponent("${encodeURIComponent(url)}");</script>
          <p>Przekierowywanie... <a href="${url}">Kliknij tutaj jeśli nie zostałeś przekierowany</a></p>
        </body>
      </html>
    `;
    const blob = new Blob([redirectHtml], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    
    const newWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");
    
    // Clean up blob URL after a delay
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    
    if (!newWindow) {
      toast.error(PL.popupBlocked);
    }
  };

  const saveSearch = async () => {
    if (!presetName.trim()) {
      toast.error(PL.enterPresetName);
      return;
    }

    try {
      const cleanCriteria = {};
      Object.entries(criteria).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          if (["price_min", "price_max", "year_min", "year_max", "mileage_min", "mileage_max", "hp_min", "hp_max", "radius"].includes(key)) {
            cleanCriteria[key] = parseInt(value);
          } else {
            cleanCriteria[key] = value;
          }
        }
      });

      await axios.post(`${API}/searches`, {
        name: presetName,
        criteria: cleanCriteria
      });

      toast.success(PL.searchSaved);
      setPresetName("");
      setSaveDialogOpen(false);
      fetchSavedSearches();
    } catch (error) {
      toast.error("Nie udało się zapisać wyszukiwania");
    }
  };

  const loadSearch = (search) => {
    const loadedCriteria = {
      brand: search.criteria.brand || "",
      model: search.criteria.model || "",
      price_min: search.criteria.price_min?.toString() || "",
      price_max: search.criteria.price_max?.toString() || "",
      year_min: search.criteria.year_min?.toString() || "",
      year_max: search.criteria.year_max?.toString() || "",
      mileage_min: search.criteria.mileage_min?.toString() || "",
      mileage_max: search.criteria.mileage_max?.toString() || "",
      fuel_type: search.criteria.fuel_type || "",
      gearbox: search.criteria.gearbox || "",
      hp_min: search.criteria.hp_min?.toString() || "",
      hp_max: search.criteria.hp_max?.toString() || "",
      location: search.criteria.location || "",
      radius: search.criteria.radius?.toString() || "",
      seller_type: search.criteria.seller_type || ""
    };
    setCriteria(loadedCriteria);
    toast.success(`${PL.loaded}: ${search.name}`);
  };

  const deleteSearch = async (id) => {
    try {
      await axios.delete(`${API}/searches/${id}`);
      toast.success(PL.searchDeleted);
      fetchSavedSearches();
    } catch (error) {
      toast.error("Nie udało się usunąć wyszukiwania");
    }
  };

  const resetForm = () => {
    setCriteria({
      brand: "",
      model: "",
      price_min: "",
      price_max: "",
      year_min: "",
      year_max: "",
      mileage_min: "",
      mileage_max: "",
      fuel_type: "",
      gearbox: "",
      hp_min: "",
      hp_max: "",
      location: "",
      radius: "",
      seller_type: ""
    });
    setGeneratedUrls(null);
    setModels([]);
  };

  // Platform configurations
  const platforms = [
    { 
      key: "mobile_de", 
      name: "mobile.de", 
      color: "from-orange-500 to-amber-500",
      bgColor: "bg-orange-500/10",
      borderColor: "border-orange-500/30",
      textColor: "text-orange-400",
      icon: "🚗",
      limitation: null
    },
    { 
      key: "kleinanzeigen", 
      name: "Kleinanzeigen", 
      color: "from-green-500 to-emerald-500",
      bgColor: "bg-green-500/10",
      borderColor: "border-green-500/30",
      textColor: "text-green-400",
      icon: "📢",
      limitation: PL.kleinanzeigenLimit
    },
    { 
      key: "autoscout24", 
      name: "AutoScout24", 
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/30",
      textColor: "text-blue-400",
      icon: "🔍",
      limitation: null
    }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 35 }, (_, i) => currentYear - i);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Background effect */}
      <div 
        className="fixed inset-0 opacity-20 mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1769641240938-1125d7421364?crop=entropy&cs=srgb&fm=jpg&ixid=M3w0NTYxODd8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBjYXIlMjBzaWxob3VldHRlJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzcxMDE2NTIxfDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(80px)"
        }}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/hellboys-garage-logo.jpg" 
              alt="Hellboy's Garage" 
              className="h-12 md:h-14 rounded-lg object-contain"
            />
            <div className="hidden sm:block">
              <h1 className="font-heading text-lg md:text-xl font-bold tracking-tight text-white">
                {PL.appName}
              </h1>
              <p className="text-xs text-zinc-500 tracking-widest uppercase">{PL.appSubtitle}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={resetForm}
            className="border-zinc-700 bg-transparent hover:bg-zinc-800 text-zinc-300"
            data-testid="reset-form-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {PL.reset}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Filters Column - 3 cols */}
          <div className="lg:col-span-3 space-y-6">
            {/* Vehicle Section */}
            <Card className="bg-surface/80 backdrop-blur-md border-white/5 shadow-2xl" data-testid="vehicle-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-heading text-white">
                  <Car className="w-5 h-5 text-orange-500" />
                  {PL.vehicle}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.brand}</Label>
                    <Select 
                      value={criteria.brand} 
                      onValueChange={(val) => handleInputChange("brand", val)}
                    >
                      <SelectTrigger className="h-12 bg-surface border-zinc-800 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50" data-testid="brand-select">
                        <SelectValue placeholder={PL.selectBrand} />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[300px]">
                        <ScrollArea className="h-[300px]">
                          {brands.map((brand) => (
                            <SelectItem 
                              key={brand} 
                              value={brand}
                              className="focus:bg-zinc-800 focus:text-white"
                            >
                              {brand}
                            </SelectItem>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.model}</Label>
                    <Select 
                      value={criteria.model} 
                      onValueChange={(val) => handleInputChange("model", val)}
                      disabled={!criteria.brand}
                    >
                      <SelectTrigger className="h-12 bg-surface border-zinc-800 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50" data-testid="model-select">
                        <SelectValue placeholder={criteria.brand ? PL.selectModel : PL.selectBrandFirst} />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[300px]">
                        <ScrollArea className="h-[300px]">
                          {models.map((model) => (
                            <SelectItem 
                              key={model} 
                              value={model}
                              className="focus:bg-zinc-800 focus:text-white"
                            >
                              {model}
                            </SelectItem>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Price & Year Section */}
            <Card className="bg-surface/80 backdrop-blur-md border-white/5 shadow-2xl" data-testid="price-year-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-heading text-white">
                  <Calendar className="w-5 h-5 text-orange-500" />
                  {PL.priceAndYear}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.minPrice}</Label>
                    <Input 
                      type="number"
                      placeholder="0"
                      value={criteria.price_min}
                      onChange={(e) => handleInputChange("price_min", e.target.value)}
                      className="h-12 bg-surface border-zinc-800 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50"
                      data-testid="price-min-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.maxPrice}</Label>
                    <Input 
                      type="number"
                      placeholder="100000"
                      value={criteria.price_max}
                      onChange={(e) => handleInputChange("price_max", e.target.value)}
                      className="h-12 bg-surface border-zinc-800 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50"
                      data-testid="price-max-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.yearFrom}</Label>
                    <Select 
                      value={criteria.year_min} 
                      onValueChange={(val) => handleInputChange("year_min", val)}
                    >
                      <SelectTrigger className="h-12 bg-surface border-zinc-800 focus:border-orange-500" data-testid="year-min-select">
                        <SelectValue placeholder={PL.any} />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[300px]">
                        <ScrollArea className="h-[300px]">
                          {years.map((year) => (
                            <SelectItem 
                              key={year} 
                              value={year.toString()}
                              className="focus:bg-zinc-800 focus:text-white"
                            >
                              {year}
                            </SelectItem>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.yearTo}</Label>
                    <Select 
                      value={criteria.year_max} 
                      onValueChange={(val) => handleInputChange("year_max", val)}
                    >
                      <SelectTrigger className="h-12 bg-surface border-zinc-800 focus:border-orange-500" data-testid="year-max-select">
                        <SelectValue placeholder={PL.any} />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[300px]">
                        <ScrollArea className="h-[300px]">
                          {years.map((year) => (
                            <SelectItem 
                              key={year} 
                              value={year.toString()}
                              className="focus:bg-zinc-800 focus:text-white"
                            >
                              {year}
                            </SelectItem>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Engine Section */}
            <Card className="bg-surface/80 backdrop-blur-md border-white/5 shadow-2xl" data-testid="engine-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-heading text-white">
                  <Zap className="w-5 h-5 text-orange-500" />
                  {PL.engineAndTransmission}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.fuelType}</Label>
                    <Select 
                      value={criteria.fuel_type} 
                      onValueChange={(val) => handleInputChange("fuel_type", val)}
                    >
                      <SelectTrigger className="h-12 bg-surface border-zinc-800 focus:border-orange-500" data-testid="fuel-type-select">
                        <SelectValue placeholder={PL.any} />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {fuelTypes.map((fuel) => (
                          <SelectItem 
                            key={fuel.value} 
                            value={fuel.value}
                            className="focus:bg-zinc-800 focus:text-white"
                          >
                            {fuel.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.gearbox}</Label>
                    <Select 
                      value={criteria.gearbox} 
                      onValueChange={(val) => handleInputChange("gearbox", val)}
                    >
                      <SelectTrigger className="h-12 bg-surface border-zinc-800 focus:border-orange-500" data-testid="gearbox-select">
                        <SelectValue placeholder={PL.any} />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {gearboxTypes.map((gearbox) => (
                          <SelectItem 
                            key={gearbox.value} 
                            value={gearbox.value}
                            className="focus:bg-zinc-800 focus:text-white"
                          >
                            {gearbox.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.sellerType}</Label>
                    <Select 
                      value={criteria.seller_type || "all"} 
                      onValueChange={(val) => handleInputChange("seller_type", val === "all" ? "" : val)}
                    >
                      <SelectTrigger className="h-12 bg-surface border-zinc-800 focus:border-orange-500" data-testid="seller-type-select">
                        <SelectValue placeholder={PL.any} />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem 
                          value="all"
                          className="focus:bg-zinc-800 focus:text-white"
                        >
                          {PL.sellerAll}
                        </SelectItem>
                        <SelectItem 
                          value="private"
                          className="focus:bg-zinc-800 focus:text-white"
                        >
                          {PL.sellerPrivate}
                        </SelectItem>
                        <SelectItem 
                          value="dealer"
                          className="focus:bg-zinc-800 focus:text-white"
                        >
                          {PL.sellerDealer}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.minHp}</Label>
                    <Input 
                      type="number"
                      placeholder="0"
                      value={criteria.hp_min}
                      onChange={(e) => handleInputChange("hp_min", e.target.value)}
                      className="h-12 bg-surface border-zinc-800 focus:border-orange-500"
                      data-testid="hp-min-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.maxHp}</Label>
                    <Input 
                      type="number"
                      placeholder="1000"
                      value={criteria.hp_max}
                      onChange={(e) => handleInputChange("hp_max", e.target.value)}
                      className="h-12 bg-surface border-zinc-800 focus:border-orange-500"
                      data-testid="hp-max-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.minMileage}</Label>
                    <Input 
                      type="number"
                      placeholder="0"
                      value={criteria.mileage_min}
                      onChange={(e) => handleInputChange("mileage_min", e.target.value)}
                      className="h-12 bg-surface border-zinc-800 focus:border-orange-500"
                      data-testid="mileage-min-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.maxMileage}</Label>
                    <Input 
                      type="number"
                      placeholder="300000"
                      value={criteria.mileage_max}
                      onChange={(e) => handleInputChange("mileage_max", e.target.value)}
                      className="h-12 bg-surface border-zinc-800 focus:border-orange-500"
                      data-testid="mileage-max-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Location Section */}
            <Card className="bg-surface/80 backdrop-blur-md border-white/5 shadow-2xl" data-testid="location-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-heading text-white">
                  <MapPin className="w-5 h-5 text-orange-500" />
                  {PL.location}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.postalCode}</Label>
                    <Input 
                      type="text"
                      placeholder="np. 10115"
                      value={criteria.location}
                      onChange={(e) => handleInputChange("location", e.target.value)}
                      className="h-12 bg-surface border-zinc-800 focus:border-orange-500"
                      data-testid="location-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.radius}</Label>
                    <Select 
                      value={criteria.radius} 
                      onValueChange={(val) => handleInputChange("radius", val)}
                    >
                      <SelectTrigger className="h-12 bg-surface border-zinc-800 focus:border-orange-500" data-testid="radius-select">
                        <SelectValue placeholder={PL.anyDistance} />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {[10, 20, 30, 50, 75, 100, 150, 200, 300, 500].map((radius) => (
                          <SelectItem 
                            key={radius} 
                            value={radius.toString()}
                            className="focus:bg-zinc-800 focus:text-white"
                          >
                            {radius} km
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Results & Actions Column - 2 cols */}
          <div className="lg:col-span-2 space-y-6">
            {/* Generate URL Card */}
            <Card className="bg-surface/80 backdrop-blur-md border-white/5 shadow-2xl sticky top-24" data-testid="generate-url-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-heading text-white">
                  <Search className="w-5 h-5 text-orange-500" />
                  {PL.searchAllPlatforms}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  onClick={generateUrl}
                  disabled={isLoading}
                  className="w-full h-14 bg-gradient-to-r from-orange-500 via-green-500 to-blue-500 hover:from-orange-600 hover:via-green-600 hover:to-blue-600 text-white font-bold uppercase tracking-wide shadow-glow transition-all duration-300"
                  data-testid="generate-url-btn"
                >
                  {isLoading ? (
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Search className="w-5 h-5 mr-2" />
                  )}
                  {PL.generateAllUrls}
                </Button>

                {generatedUrls && (
                  <div className="animate-fade-in space-y-4">
                    <Separator className="bg-zinc-800" />
                    <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.generatedLinks}</Label>
                    
                    {/* Platform Links */}
                    <div className="space-y-3">
                      {platforms.map((platform) => (
                        <div 
                          key={platform.key}
                          className={`p-4 rounded-lg border ${platform.borderColor} ${platform.bgColor} transition-all hover:scale-[1.02]`}
                          data-testid={`platform-${platform.key}`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{platform.icon}</span>
                              <span className={`font-bold ${platform.textColor}`}>{platform.name}</span>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => copyToClipboard(generatedUrls[platform.key], platform.name)}
                                className={`h-8 w-8 hover:${platform.bgColor}`}
                                data-testid={`copy-${platform.key}-btn`}
                              >
                                <Copy className={`w-4 h-4 ${platform.textColor}`} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openInNewTab(generatedUrls[platform.key])}
                                className={`h-8 w-8 hover:${platform.bgColor}`}
                                data-testid={`open-${platform.key}-btn`}
                              >
                                <ExternalLink className={`w-4 h-4 ${platform.textColor}`} />
                              </Button>
                            </div>
                          </div>
                          <div 
                            className="text-xs font-mono text-zinc-400 break-all line-clamp-2 cursor-pointer hover:text-zinc-300"
                            onClick={() => openInNewTab(generatedUrls[platform.key])}
                          >
                            {generatedUrls[platform.key]}
                          </div>
                          {platform.limitation && (
                            <div className="mt-2 text-xs text-amber-500/80 bg-amber-500/10 px-2 py-1.5 rounded border border-amber-500/20">
                              ⚠️ {platform.limitation}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Open All Button */}
                    <Button 
                      variant="outline"
                      onClick={() => {
                        platforms.forEach((p) => openInNewTab(generatedUrls[p.key]));
                      }}
                      className="w-full border-zinc-700 bg-transparent hover:bg-zinc-800 text-zinc-300"
                      data-testid="open-all-btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {PL.openAllInNewTabs}
                    </Button>

                    {/* Save Search */}
                    <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="outline"
                          className="w-full border-orange-500/50 bg-transparent hover:bg-orange-500/10 text-orange-500"
                          data-testid="save-search-btn"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {PL.saveSearchCriteria}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
                        <DialogHeader>
                          <DialogTitle className="font-heading">{PL.saveSearchPreset}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <div className="space-y-2">
                            <Label className="text-xs font-bold tracking-widest uppercase text-zinc-500">{PL.presetName}</Label>
                            <Input 
                              placeholder={PL.presetPlaceholder}
                              value={presetName}
                              onChange={(e) => setPresetName(e.target.value)}
                              className="h-12 bg-surface border-zinc-800 focus:border-orange-500"
                              data-testid="preset-name-input"
                            />
                          </div>
                          <Button 
                            onClick={saveSearch}
                            className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold"
                            data-testid="confirm-save-btn"
                          >
                            <Save className="w-4 h-4 mr-2" />
                            {PL.savePreset}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Saved Searches Card */}
            <Card className="bg-surface/80 backdrop-blur-md border-white/5 shadow-2xl" data-testid="saved-searches-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-heading text-white">
                  <Bookmark className="w-5 h-5 text-orange-500" />
                  {PL.savedSearches}
                  {savedSearches.length > 0 && (
                    <span className="ml-auto text-sm font-normal text-zinc-500">
                      {savedSearches.length} {PL.saved}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {savedSearches.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <Bookmark className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">{PL.noSavedSearches}</p>
                    <p className="text-xs mt-1">{PL.generateAndSave}</p>
                  </div>
                ) : (
                  <ScrollArea className="max-h-[400px]">
                    <div className="space-y-2">
                      {savedSearches.map((search) => (
                        <div 
                          key={search.id}
                          className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-800 hover:border-zinc-700 transition-colors group"
                        >
                          <div className="flex items-center justify-between">
                            <div 
                              className="flex-1 cursor-pointer"
                              onClick={() => loadSearch(search)}
                            >
                              <h4 className="font-medium text-white group-hover:text-orange-400 transition-colors" data-testid={`saved-search-${search.id}`}>
                                {search.name}
                              </h4>
                              <p className="text-xs text-zinc-500 mt-1">
                                {search.criteria.brand && `${search.criteria.brand}`}
                                {search.criteria.model && ` ${search.criteria.model}`}
                                {search.criteria.price_max && ` • ${PL.upTo} €${search.criteria.price_max.toLocaleString()}`}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteSearch(search.id)}
                              className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-500 hover:bg-red-500/10 transition-all"
                              data-testid={`delete-search-${search.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 mt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 text-center">
          <p className="text-zinc-600 text-sm">
            {PL.footerText}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SearchBuilder;
